import React, {createRef, ReactNode, RefObject} from "react";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {AdminDialogsGlobals} from "../dialog/AdminDialogs";
import {Button} from "primereact/button";
import Resizeable, {ResizeableDirection} from "../../shared/components/Resizeable";
import {AdminGlobals} from "../Admin";
import ContentMode from "../ContentMode";
import InfoPanel from "./InfoPanel";
import WebservicesPanel from "./WebservicesPanel";
import StatisticsPanel from "./StatisticsPanel";
import LogsPanel from "./LogsPanel";
import SupportPanel from "./SupportPanel";
import Translate from "../../shared/translation/Translate";
import DocumentationPanel from "./DocumentationPanel";
import {HelperGlobals} from "../../shared/components/Helper";
import SessionPanel from "./SessionPanel";
import {AdminDebugGlobals} from "../dialog/components/AdminDebugDialog";
import ConnectionPanel from "./ConnectionPanel";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export interface AdminScreenProperties extends AdminGlobals, AdminDialogsGlobals, I18nGlobals, HelperGlobals,
	AdminDebugGlobals {
}

export interface AdminScreenState {
	menuActive: boolean;
	menuWidth: string;
}

export class AdminScreenDefinition extends PortalPureComponent<AdminScreenProperties, AdminScreenState> {
	private resizeContainer: RefObject<HTMLDivElement>;

	constructor(props: Readonly<AdminScreenProperties>) {
		super(props);

		this.state = {
			menuActive: true,
			menuWidth: "20%"
		};

		this.resizeContainer = createRef<HTMLDivElement>();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<div className="admin-screen" ref={this.resizeContainer}>
				<div className={"sidebar left" + (this.state.menuActive ? " in" : "")}
					 data-help="global.screen.admin.sidebar" data-position="right"
					 style={{width: this.state.menuWidth}}>
					<Button className="toggle-btn"
							icon={"icon " + (this.state.menuActive ? "pi-angle-left" : "pi-angle-right")}
							onClick={() => this.setState({
								menuActive: !this.state.menuActive
							})}/>
					<Resizeable direction={ResizeableDirection.HORIZONTAL} container={this.resizeContainer}
								invert={false}
								resizeCallback={(options: { width: string }) => this.setState({
									menuWidth: options.width
								})}/>
					<div className="body">
						<Button icon="icon icon-info"
								className={this.props.contentMode === ContentMode.INFO ? "active" : undefined}
								label={Translate.value("administration.info", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.INFO
								})}/>
						<Button icon="icon icon-connection"
								className={this.props.contentMode === ContentMode.CONNECTION ? "active" : undefined}
								label={Translate.value("administration.connection", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.CONNECTION
								})}/>
						<Button icon="icon icon-webservices"
								className={this.props.contentMode === ContentMode.WEBSERVICES ? "active" : undefined}
								label={Translate.value("administration.webservices", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.WEBSERVICES
								})}/>
						<Button icon="icon icon-statistic"
								className={this.props.contentMode === ContentMode.STATISTICS ? "active" : undefined}
								label={Translate.value("administration.statistics", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.STATISTICS
								})}/>
						<Button icon="icon icon-sessions"
								className={this.props.contentMode === ContentMode.SESSIONS ? "active" : undefined}
								label={Translate.value("administration.sessions", {ns: "menu"})}
								onClick={async () => this.props.context.getPage().setState({
									contentMode: ContentMode.SESSIONS
								})}/>
						<Button icon="icon icon-list"
								className={this.props.contentMode === ContentMode.LOGS ? "active" : undefined}
								label={Translate.value("administration.logs", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.LOGS
								})}/>
						<Button icon="icon icon-support"
								className={this.props.contentMode === ContentMode.SUPPORT ? "active" : undefined}
								label={Translate.value("administration.support", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.SUPPORT
								})}/>
						<Button icon="icon icon-documentation"
								className={this.props.contentMode === ContentMode.DOCUMENTATION ? "active" : undefined}
								label={Translate.value("administration.documentation", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									contentMode: ContentMode.DOCUMENTATION
								})}/>
					</div>
				</div>

				{this.props.contentMode === ContentMode.INFO && <InfoPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.WEBSERVICES && <WebservicesPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.STATISTICS && <StatisticsPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.SESSIONS && <SessionPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.LOGS && <LogsPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.SUPPORT && <SupportPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.DOCUMENTATION && <DocumentationPanel {...this.props}/>}
				{this.props.contentMode === ContentMode.CONNECTION && <ConnectionPanel {...this.props}/>}
			</div>
		);
	}
}

const AdminScreen = React.memo(AdminScreenDefinition,
	(prevProps: Readonly<AdminScreenProperties>, nextProps: Readonly<AdminScreenProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!prevProps.serverStatus?.equals(nextProps.serverStatus)) {
			return false;
		}

		if (!prevProps.sessionTable?.equals(nextProps.sessionTable)) {
			return false;
		}

		if (!prevProps.applicationConfig?.equals(nextProps.applicationConfig)) {
			return false;
		}

		if (!prevProps.logConfig?.equals(nextProps.logConfig)) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default AdminScreen;